/**
 * In-person tutors.
 */

import { apiClient } from '@/services/api-client';
import { XLSX_MIME } from '@/constants/mime-types.constants';
import { toLocalDateString } from '@/utils/format-date-utils';
import type {
  ProfileDto,
  ListFilters,
  PaginatedList,
  TutorAssignment,
} from '@/types/inperson/tutor.types';
import type { GetUsersParams, UserWithProfile, WithPhoneSetupLink } from '@/types/user.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Normalize a Date or YYYY-MM-DD string for tutor list query params. */
function toQueryDate(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return toLocalDateString(value);
  }
  const ymd = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : undefined;
}

/** `GET /users/tutors` — paginated in-person tutors. */
export const getTutors = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));
  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  const dateFrom = toQueryDate(params?.dateFrom);
  const dateTo = toQueryDate(params?.dateTo);
  if (dateFrom) queryParams.append('dateFrom', dateFrom);
  if (dateTo) queryParams.append('dateTo', dateTo);
  params?.availabilityDays?.forEach((d) => queryParams.append('availabilityDays', String(d)));
  if (params?.engagementStartDateFrom)
    queryParams.append('engagementStartDateFrom', params.engagementStartDateFrom);
  if (params?.engagementStartDateTo)
    queryParams.append('engagementStartDateTo', params.engagementStartDateTo);
  if (params?.engagementEndDateFrom)
    queryParams.append('engagementEndDateFrom', params.engagementEndDateFrom);
  if (params?.engagementEndDateTo)
    queryParams.append('engagementEndDateTo', params.engagementEndDateTo);
  if (params?.tutorExitDateFrom) queryParams.append('tutorExitDateFrom', params.tutorExitDateFrom);
  if (params?.tutorExitDateTo) queryParams.append('tutorExitDateTo', params.tutorExitDateTo);
  if (params?.tutorExitReason) queryParams.append('tutorExitReason', params.tutorExitReason);
  const { data } = await apiClient.get(`/users/tutors?${queryParams.toString()}`);
  return data;
};

/** `GET /users/tutors/:identifier` */
export const getTutor = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/tutors/${encodeURIComponent(identifier)}`);
  return data;
};

/** `POST /tutors-presentiel` — create tutor + onboarding record. */
export const createProfile = async (
  dto: ProfileDto,
  companyId: string,
): Promise<{ user: unknown; record: unknown }> => {
  const response = await apiClient.post<{ user: unknown; record: unknown }>(
    `/tutors-presentiel?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** `PATCH /tutors-presentiel/:userId` */
export const updateProfile = async (
  userId: string,
  dto: ProfileDto,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<unknown>>(
    `/tutors-presentiel/${userId}?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** `GET /onboarding/tutor-presentiel/:userId` */
export const getFullState = async (userId: string, companyId: string): Promise<unknown> => {
  const response = await apiClient.get(
    `/onboarding/tutor-presentiel/${userId}?companyId=${companyId}`,
  );
  return response.data;
};

/** `GET /onboarding/tutor-presentiel/establishments/eligible` */
export const getEligibleEstablishments = async (
  departments: string[],
  companyId: string,
): Promise<unknown[]> => {
  const deptParam = departments.join(',');
  const response = await apiClient.get<unknown[]>(
    `/onboarding/tutor-presentiel/establishments/eligible?companyId=${companyId}&departments=${deptParam}&isProspectionValidated=true`,
  );
  return response.data;
};

/** Set a query param only when the value is non-empty. */
function setDefined(params: Record<string, unknown>, key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') return;
  if (Array.isArray(value) && value.length === 0) return;
  params[key] = value;
}

/** Build list query params from tutor list filters. */
function buildListQueryParams(
  filters: ListFilters | Omit<ListFilters, 'page' | 'pageSize'>,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  setDefined(params, 'tabView', filters.tabView);
  setDefined(params, 'search', filters.search);
  if ('page' in filters) setDefined(params, 'page', filters.page);
  if ('pageSize' in filters) setDefined(params, 'pageSize', filters.pageSize);
  setDefined(params, 'registrationManagerId', filters.registrationManagerId);
  setDefined(params, 'departmentCode', filters.departmentCode);
  setDefined(params, 'departmentCodes', filters.departmentCodes);
  setDefined(params, 'establishmentId', filters.establishmentId);
  setDefined(params, 'registrationDateFrom', filters.registrationDateFrom);
  setDefined(params, 'registrationDateTo', filters.registrationDateTo);
  setDefined(params, 'statuses', filters.statuses);
  setDefined(params, 'tutorSituation', filters.tutorSituation);
  setDefined(params, 'subjectIds', filters.subjectIds);
  setDefined(params, 'availabilityDays', filters.availabilityDays);
  setDefined(params, 'engagementStartDateFrom', filters.engagementStartDateFrom);
  setDefined(params, 'engagementStartDateTo', filters.engagementStartDateTo);
  setDefined(params, 'engagementEndDateFrom', filters.engagementEndDateFrom);
  setDefined(params, 'engagementEndDateTo', filters.engagementEndDateTo);
  setDefined(params, 'exitDateFrom', filters.exitDateFrom);
  setDefined(params, 'exitDateTo', filters.exitDateTo);
  setDefined(params, 'exitReason', filters.exitReason);
  setDefined(params, 'sansRi', filters.sansRi || undefined);
  setDefined(params, 'sansCj', filters.sansCj || undefined);
  setDefined(params, 'programId', filters.programId);
  return params;
}

/** `GET /tutors-presentiel` — paginated onboarding list. */
export const list = async (filters: ListFilters): Promise<PaginatedList> => {
  const params = buildListQueryParams(filters);
  const response = await apiClient.get<PaginatedList>('/tutors-presentiel', { params });
  return response.data;
};

/** `GET /tutors-presentiel/export` — download XLSX. */
export const exportExcel = async (
  filters: Omit<ListFilters, 'page' | 'pageSize'>,
): Promise<void> => {
  const params = buildListQueryParams(filters);
  const response = await apiClient.get('/tutors-presentiel/export', {
    params,
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tutors-presentiel-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/** `PATCH /tutors-presentiel/:userId` with `sessionId` after joining an RI. */
export const reserveInformationSession = async (
  userId: string,
  companyId: string,
  sessionId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  return updateProfile(userId, { sessionId }, companyId);
};

/** `PATCH /tutors-presentiel/:userId` with `sessionId: null` after canceling an RI booking. */
export const clearInformationSessionReservation = async (
  userId: string,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  return updateProfile(userId, { sessionId: null }, companyId);
};

/** `POST /onboarding/tutor-presentiel/:userId/information-session/participate` */
export const markInformationMeetingParticipation = async (
  userId: string,
  companyId: string,
  params: { sessionId?: string; participated?: boolean; participatedAt?: string },
): Promise<unknown> => {
  const response = await apiClient.post(
    `/onboarding/tutor-presentiel/${userId}/information-session/participate?companyId=${companyId}`,
    params,
  );
  return response.data;
};

/** `GET /tutors-presentiel/:userId/affectations` */
export const listAssignments = async (
  userId: string,
  companyId: string,
): Promise<TutorAssignment[]> => {
  const response = await apiClient.get<TutorAssignment[]>(
    `/tutors-presentiel/${userId}/affectations?companyId=${companyId}`,
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/retain` */
export const retainEstablishments = async (
  userId: string,
  companyId: string,
  establishmentIds: string[],
): Promise<TutorAssignment[]> => {
  const response = await apiClient.post<TutorAssignment[]>(
    `/tutors-presentiel/${userId}/affectations/retain?companyId=${companyId}`,
    { establishmentIds },
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/refuse-all` */
export const refuseAllEstablishments = async (
  userId: string,
  companyId: string,
  reason: string,
  establishmentIds?: string[],
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>(
    `/tutors-presentiel/${userId}/affectations/refuse-all?companyId=${companyId}`,
    { reason, establishmentIds },
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/reassign` */
export const reassignEstablishment = async (
  userId: string,
  companyId: string,
  establishmentId: string,
): Promise<TutorAssignment> => {
  const response = await apiClient.post<TutorAssignment>(
    `/tutors-presentiel/${userId}/affectations/reassign?companyId=${companyId}`,
    { establishmentId },
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/:affectationId/validate` */
export const validateAssignment = async (
  userId: string,
  companyId: string,
  affectationId: string,
  comments?: string,
): Promise<TutorAssignment> => {
  const response = await apiClient.post<TutorAssignment>(
    `/tutors-presentiel/${userId}/affectations/${affectationId}/validate?companyId=${companyId}`,
    { comments },
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/:affectationId/refuse` */
export const refuseAssignment = async (
  userId: string,
  companyId: string,
  affectationId: string,
  reason: string,
): Promise<TutorAssignment> => {
  const response = await apiClient.post<TutorAssignment>(
    `/tutors-presentiel/${userId}/affectations/${affectationId}/refuse?companyId=${companyId}`,
    { reason },
  );
  return response.data;
};

/** `POST /tutors-presentiel/:userId/affectations/direct-assign` */
export const coordinatorDirectAssign = async (
  userId: string,
  companyId: string,
  establishmentId: string,
  comments?: string,
): Promise<TutorAssignment> => {
  const response = await apiClient.post<TutorAssignment>(
    `/tutors-presentiel/${userId}/affectations/direct-assign?companyId=${companyId}`,
    { establishmentId, comments },
  );
  return response.data;
};
